import { FC, useState } from 'react';
import { ColorPicker, type ColorPickerProps } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import PlatformSelect from '../PlatformSelect';

type ColorFormat = NonNullable<ColorPickerProps['format']>;

const colorFormatOptions: Array<{ label: string; value: ColorFormat }> = [
    { label: 'HEX', value: 'hex' },
    { label: 'HSB', value: 'hsb' },
    { label: 'RGB', value: 'rgb' },
];

/**
 * Keeps color-picker popovers and format selection aligned with platform
 * dropdown conventions without overriding Ant Design's generated internals.
 */
const PlatformColorPicker: FC<ColorPickerProps> = ({
    arrow = false,
    defaultFormat = 'hex',
    disabledFormat = false,
    format,
    onFormatChange,
    panelRender,
    styles,
    ...props
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [uncontrolledFormat, setUncontrolledFormat] = useState<ColorFormat>(defaultFormat);
    const mergedFormat = format ?? uncontrolledFormat;

    const handleFormatChange = (nextFormat: ColorFormat) => {
        if (format === undefined) {
            setUncontrolledFormat(nextFormat);
        }

        onFormatChange?.(nextFormat);
    };

    const renderPlatformPanel: NonNullable<ColorPickerProps['panelRender']> = (panel, extra) => {
        const platformPanel = disabledFormat ? (
            panel
        ) : (
            <>
                <div
                    style={{
                        alignItems: 'center',
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.75rem',
                        paddingBottom: '0.5rem',
                    }}
                >
                    <small style={{ color: token.colorTextTertiary }}>Color format</small>
                    <PlatformSelect<ColorFormat>
                        aria-label='Color format'
                        getPopupContainer={(triggerNode) => triggerNode.parentElement ?? triggerNode}
                        options={colorFormatOptions}
                        popupMatchSelectWidth={68}
                        size='small'
                        value={mergedFormat}
                        variant='borderless'
                        style={{ width: '4.5rem' }}
                        onChange={handleFormatChange}
                    />
                </div>
                {panel}
            </>
        );

        return panelRender ? panelRender(platformPanel, extra) : platformPanel;
    };

    return (
        <ColorPicker
            {...props}
            arrow={arrow}
            disabledFormat
            format={mergedFormat}
            panelRender={renderPlatformPanel}
            styles={{
                popup: {
                    paddingTop: '0.375rem',
                    ...styles?.popup,
                },
                popupOverlayInner: {
                    padding: '0.75rem',
                    ...styles?.popupOverlayInner,
                },
            }}
        />
    );
};

export default PlatformColorPicker;
